import { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { setAlert } from 'src/typescript/components/alert/actions';
import Button from 'src/components/buttons/button';
import i18n from 'src/utils/translate';
import * as log from 'loglevel';
import modalTypes from 'src/constants/modalTypes';
import { showModal } from 'src/modules/shared/modal/actions';
import EventType from 'src/constants/footfallEventTypes';
import { trackFootfallEvent } from 'src/utils/footfall';

class ImageUpload extends Component<any, any> {
  static propTypes = {
    currentImageUrl: PropTypes.string.isRequired,
    clickTypeLabel: PropTypes.string,
    boundary: PropTypes.object,
    className: PropTypes.string,
    communitySlug: PropTypes.string,
    fitBest: PropTypes.bool,
    fitHeight: PropTypes.bool,
    fitWidth: PropTypes.bool,
    isEditProfile: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onImageSelect: PropTypes.func,
    savePreviewLocally: PropTypes.bool,
  };

  static defaultProps = {
    boundary: { height: 130, width: 130 },
    clickTypeLabel: '',
    communitySlug: null,
    fitBest: false,
    fitHeight: false,
    fitWidth: false,
    isEditProfile: false,
    savePreviewLocally: false,
    onImageSelect: () => {},
  };

  dropzoneRef: any;
  lastPos: any;

  constructor(props) {
    super(props);
    this.state = {
      previewImageUrl: null,
      imageDimensions: {
        width: 0,
        height: 0,
      },
      currentImageUrl: props.currentImageUrl,
      grabbing: false,
      imagePos: {
        x: 0,
        y: 0,
      },
      isWidthMaster: false,
      isHeightMaster: false,
      fileUpload: null,
    };
    this.dropzoneRef = createRef();
  }

  onDrop = (files) => {
    this.handleImageUpload(files[0]);
  }

  handleImageUpload = async (file) => {
    try {
      if (this.props.savePreviewLocally) {
        const imageUrl = URL.createObjectURL(file);
        this.setState({ previewImageUrl: imageUrl, fileUpload: file });
      } else {
        const formData = new FormData();
        formData.append('file', file);
        const { imageUrl } = await this.props.onImageSelect(formData);
        this.setState({ previewImageUrl: imageUrl });
      }
    } catch (ex) {
      log.error(ex);
      this.props.setAlert(i18n.t('Whoops! Looks like there was a problem uploading your image.'));
    }
  }

  onPreviewImageLoaded = (event) => {
    const { naturalWidth, naturalHeight } = event.target;

    let imageWidthScale = 1;
    let imageHeightScale = 1;
    let isWidthMaster = false;
    let isHeightMaster = false;

    // - 'fit best' mode works out the smallest an image can go while remaining ratio-maintaining
    // to always cover the boundary area
    // - 'fit width' will ensure the width is always 100% and adjust the height accordingly
    // - 'fit height' does the same but with height ofc.
    if (this.props.fitBest) {
      imageWidthScale = this.props.boundary.width / naturalWidth;
      imageHeightScale = this.props.boundary.height / naturalHeight;
      if (imageWidthScale >= imageHeightScale) {
        isWidthMaster = true;
        imageHeightScale = imageWidthScale;
      } else {
        isHeightMaster = true;
        imageWidthScale = imageHeightScale;
      }
    } else {
      // Calculate width scale (and modify height for ratio)
      if (this.props.fitWidth) {
        imageWidthScale = this.props.boundary.width / naturalWidth;
        isWidthMaster = true;

        // Keep ratio
        if (!this.props.fitHeight) {
          imageHeightScale = imageWidthScale;
        }
      }
      // Calculate height scale (and modify width for ratio)
      if (this.props.fitHeight) {
        imageHeightScale = this.props.boundary.height / naturalHeight;
        isHeightMaster = true;

        // Keep ratio
        if (!this.props.fitWidth) {
          imageWidthScale = imageHeightScale;
        }
      }
    }

    this.setState({
      imageDimensions: {
        width: naturalWidth,
        height: naturalHeight,
      },
      imageScale: {
        width: imageWidthScale,
        height: imageHeightScale,
      },
      isWidthMaster,
      isHeightMaster,
    });
  }

  formattedFileData = () => {
    const { x, y, width, height } = this.getFinalCoordinates();
    const formData = new FormData();
    formData.append('file', this.state.fileUpload);
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    formData.append('x', x);
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    formData.append('y', y);
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    formData.append('width', width);
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    formData.append('height', height);
    return formData;
  }

  saveAndCrop = async () => {
    try {
      if (this.props.savePreviewLocally) {
        const { imageUrl } = await this.props.onSave(this.props.communitySlug, this.formattedFileData());
        this.setState({ currentImageUrl: imageUrl, previewImageUrl: null, fileUpload: null });
      } else {
        const { imageUrl } = await this.props.onSave(this.getFinalCoordinates());
        this.setState({ currentImageUrl: imageUrl, previewImageUrl: null });
      }
      if (this.props.isEditProfile) {
        trackFootfallEvent(EventType.CLICKED, { clickType: `added-${this.props.clickTypeLabel}` });
      }
    } catch (ex) {
      log.error(ex);
      this.props.setAlert(i18n.t('Whoops! Looks like there was a problem uploading your image.'));
    }
  }

  getFinalCoordinates = () => {
    const { imageDimensions, imageScale, imagePos } = this.state;
    const { boundary } = this.props;

    const width = !this.state.isWidthMaster
      ? boundary.width / imageScale.width
      : imageDimensions.width;
    const height = !this.state.isHeightMaster
      ? boundary.height / imageScale.height
      : imageDimensions.height;

    return {
      x: Math.round(-imagePos.x / imageScale.width),
      y: Math.round(-imagePos.y / imageScale.height),
      width: Math.round(width),
      height: Math.round(height),
    };
  }

  onImageMouseDown = event => {
    this.setState({ grabbing: true });

    // Bind event handlers
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);

    // Initial mouse position
    this.lastPos = {x: event.clientX, y: event.clientY};

    event.preventDefault();
  }

  onMouseMove = (event) => {
    // Calculate min x/y values for dragging (taking into account if we are scaling the image)
    const minX = this.state.isWidthMaster ?
      0 :
      this.props.boundary.width - (this.state.imageDimensions.width * this.state.imageScale.width);
    const minY = this.state.isWeightMaster ?
      0 :
      this.props.boundary.height - (this.state.imageDimensions.height * this.state.imageScale.height);

    this.setState({
      imagePos: {
        x: Math.min(Math.max(this.state.imagePos.x + (event.clientX - this.lastPos.x), minX), 0),
        y: Math.min(Math.max(this.state.imagePos.y + (event.clientY - this.lastPos.y), minY), 0),
      },
    });

    // Set the new image pos taking into account the delta of this move
    this.lastPos = {x: event.clientX, y: event.clientY};
  }

  onMouseUp = () => {
    this.setState({grabbing: false});
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
  }

  triggerPrivacyTipsModal = (event) => {
    if (!this.props.isEditProfile) return;
    event.stopPropagation();
    trackFootfallEvent(EventType.CLICKED, { clickType: `clicked-add-${this.props.clickTypeLabel}` });
    this.props.showModal({
      modalType: modalTypes.PRIVACY_TIPS_IMAGES,
      modalProps: {
        onAccept: () => {
          // ref is set async so it might be null at some point
          if (this.dropzoneRef.current) this.dropzoneRef.current.open();
        },
      },
    });
  }

  renderActionButtons() {
    const { actionsClassName } = this.props;
    const className = classNames({
      'uploader__actions': true,
      'uploader__actions--current': !this.state.previewImageUrl,
      'uploader__actions--preview': !!this.state.previewImageUrl,
      [actionsClassName]: !!actionsClassName,
    });
    return (
      <div className={className}>
        {!this.state.previewImageUrl
          && (
            <Button
              dataTestId={this.props.dataTestId}
              icon="plus-2"
              iconClassName="edit-profile__button-icon"
              text={this.props.buttonText || i18n.t('Upload')}
              type="secondary"
            />
          )}
        {this.state.previewImageUrl
          && (
            <Fragment>
              <Button
                text={i18n.t('Save')}
                onClick={this.saveAndCrop}
              />
              <Button
                text={i18n.t('Cancel')}
                type="secondary"
                onClick={() => this.setState({ previewImageUrl: null })}
              />
            </Fragment>
          )}
      </div>
    );
  }

  renderPreview() {
    const imageStyles = {
      position: 'absolute',
      top: this.state.imagePos.y,
      left: this.state.imagePos.x,
      width: this.state.isWidthMaster ? this.props.boundary.width : null,
      height: this.state.isHeightMaster ? this.props.boundary.height : null,
    };
    return (
      <div className="uploader__preview" style={{ height: this.props.boundary.height, width: this.props.boundary.width }} >
        <img
          src={this.state.previewImageUrl}
          // @ts-expect-error ts-migrate(2322) FIXME: Type '{ position: string; top: any; left: any; wid... Remove this comment to see the full error message
          style={imageStyles}
          onLoad={this.onPreviewImageLoaded}
          onMouseDown={this.onImageMouseDown}
        />
      </div>
    );
  }

  render() {
    const className = classNames({
      [this.props.className]: !!this.props.className,
      'uploader': true,
      'uploader--grab': !!this.state.previewImageUrl,
      'uploader--grabbing': this.state.grabbing,
    });

    return (
      <div className={className} style={{ backgroundImage: `url(${this.state.currentImageUrl})` }} >
        <Dropzone
          accept="image/*"
          disabled={!!this.state.previewImageUrl}
          multiple={false}
          noDrag={true}
          ref={this.dropzoneRef}
          onDrop={this.onDrop}
        >
          {({getRootProps, getInputProps}) => (
            <div
              {...getRootProps({ onClick: this.triggerPrivacyTipsModal })}
              style={{ border: 'none' }}
            >
              <input {...getInputProps()} />
              {this.state.previewImageUrl && this.renderPreview()}
              {this.renderActionButtons()}
            </div>
          )}
        </Dropzone>
      </div>
    );
  }
}

export default connect(null, { setAlert, showModal })(ImageUpload);
